/**
 * lib/cards/layout-renderers.js — Shared element primitives.
 *
 * Chunk 1 (Apr 24 2026): jsPDF-only. Each primitive takes:
 *   - pdf: jsPDF instance
 *   - element: { id, type, x, y, w, h, z, locked, props }
 *   - content: element-type-specific runtime data
 *   - context: { brandColor, tenant, staff }
 *
 * Chunk 2 will add Konva adapters (same signature shape, different
 * `ctx` object) by introducing a small adapter layer. For Chunk 1 we
 * call these directly from render-layout.js.
 *
 * Every primitive is self-contained:
 *   - No side effects beyond drawing to the pdf
 *   - No reading from anywhere other than `element` + `content`
 *   - No knob-awareness (knobs are resolved before layout JSON is
 *     built, inside the template-*.js files)
 *
 * This is the surface that has to stay stable across Chunk 1-5 —
 * editors, template presets, and future custom-font-uploads all
 * consume these primitives.
 */

import { CR80 } from './shared.js';

/* ─────────────────────────────────────────────────────────────── */
/*  Colour resolution                                               */
/*                                                                  */
/*  Props can specify colour as:                                    */
/*    - hex string '#RRGGBB'                                        */
/*    - named 'brand'             → tenant brand colour             */
/*    - named 'auto-contrast'     → white or dark picked by WCAG    */
/*                                 luminance of element's back-     */
/*                                 ground (caller passes bg hint)   */
/* ─────────────────────────────────────────────────────────────── */

export function resolveColorProp(value, ctx, options = {}) {
  if (!value) {
    // Default to near-black for text-ish elements
    return { r: 14, g: 26, b: 21 };
  }
  if (value === 'brand') {
    return ctx.brandColor;
  }
  if (value === 'auto-contrast') {
    const bg = options.backgroundColor || ctx.brandColor;
    return contrastingRgb(bg);
  }
  return hexToRgb(value);
}

function hexToRgb(hex) {
  const s = hex.replace('#', '');
  if (s.length !== 6) return { r: 14, g: 26, b: 21 };
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}

// Relative luminance per WCAG. Returns 'light' if bg is dark (use
// white text), 'dark' if bg is light (use dark text).
function contrastingRgb(bg) {
  const norm = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * norm(bg.r) + 0.7152 * norm(bg.g) + 0.0722 * norm(bg.b);
  if (L < 0.5) return { r: 255, g: 255, b: 255 };
  return { r: 20, g: 30, b: 28 };
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: static_rect — filled rectangle                       */
/* ─────────────────────────────────────────────────────────────── */

export function renderRectTo(pdf, element, ctx) {
  const fill = resolveColorProp(element.props?.fill || 'brand', ctx);
  pdf.setFillColor(fill.r, fill.g, fill.b);

  const radius = element.props?.cornerRadius || 0;
  if (radius > 0) {
    pdf.roundedRect(element.x, element.y, element.w, element.h, radius, radius, 'F');
  } else {
    pdf.rect(element.x, element.y, element.w, element.h, 'F');
  }

  // Optional stroke
  if (element.props?.stroke) {
    const stroke = resolveColorProp(element.props.stroke, ctx);
    const thickness = element.props.strokeWidth || 0.2;
    pdf.setDrawColor(stroke.r, stroke.g, stroke.b);
    pdf.setLineWidth(thickness);
    if (radius > 0) {
      pdf.roundedRect(element.x, element.y, element.w, element.h, radius, radius, 'S');
    } else {
      pdf.rect(element.x, element.y, element.w, element.h, 'S');
    }
  }
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: static_divider — thin line                           */
/* ─────────────────────────────────────────────────────────────── */

export function renderDividerTo(pdf, element, ctx) {
  const color = resolveColorProp(element.props?.color || 'brand', ctx);
  const thickness = element.props?.thickness || 0.3;
  pdf.setDrawColor(color.r, color.g, color.b);
  pdf.setLineWidth(thickness);

  const orientation = element.props?.orientation || 'horizontal';
  if (orientation === 'horizontal') {
    const y = element.y + element.h / 2;
    pdf.line(element.x, y, element.x + element.w, y);
  } else {
    const x = element.x + element.w / 2;
    pdf.line(x, element.y, x, element.y + element.h);
  }
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: text (static_text | dynamic_*_text)                  */
/*                                                                  */
/*  Content: { text: string }                                       */
/*                                                                  */
/*  jsPDF text renders baseline-down. We want box-aligned. Vertical */
/*  alignment uses fontSize as a proxy for line height (same heuri- */
/*  stic the current template files use).                           */
/* ─────────────────────────────────────────────────────────────── */

export function renderTextTo(pdf, element, content, ctx) {
  if (!content?.text) return;

  const props = element.props || {};
  const fontFamily = props.fontFamily || 'helvetica';
  const fontWeight = props.fontWeight || 'normal';
  const fontSize = props.fontSize || 10;
  const color = resolveColorProp(props.color, ctx);
  const align = props.align || 'left';
  const charSpace = props.charSpace || 0;
  const uppercase = props.uppercase === true;
  const truncate = props.truncate !== false; // default true

  let text = content.text;
  if (uppercase) text = text.toUpperCase();

  pdf.setFont(fontFamily, fontWeight);
  pdf.setFontSize(fontSize);
  pdf.setTextColor(color.r, color.g, color.b);

  // Truncate to element width (single-line only in v1 — see spec 5.3)
  let rendered = text;
  if (truncate) {
    const lines = pdf.splitTextToSize(text, element.w);
    rendered = lines[0] || '';
  }

  // Horizontal anchor
  let tx;
  if (align === 'center') tx = element.x + element.w / 2;
  else if (align === 'right') tx = element.x + element.w;
  else tx = element.x;

  // Vertical anchor — baseline placement
  //
  // Two modes:
  //   baselineAnchor:true  → element.y is TREATED AS THE BASELINE
  //                          directly. Used by the template-*.js
  //                          refactor to match the current hard-
  //                          coded pdf.text() y-values byte-for-
  //                          byte. Editor-drawn elements use the
  //                          box model below.
  //   (default)            → element.y is the top of the box;
  //                          verticalAlign places baseline within.
  //
  // fontSize in jsPDF is in pt; 1pt ≈ 0.3528mm. capHeight ≈ 0.7 ×
  // fontSize pt used as a proxy for visual text height.
  const verticalAlign = props.verticalAlign || 'top';
  const capHeightMm = fontSize * 0.7 * 0.3528;

  let ty;
  if (props.baselineAnchor === true) {
    ty = element.y;
  } else if (verticalAlign === 'center') {
    ty = element.y + element.h / 2 + capHeightMm / 2;
  } else if (verticalAlign === 'bottom') {
    ty = element.y + element.h;
  } else {
    // 'top' — baseline sits one capHeight below element top
    ty = element.y + capHeightMm;
  }

  pdf.text(rendered, tx, ty, { align, charSpace });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: image box (dynamic_photo | dynamic_logo |            */
/*             dynamic_banner | static_image)                       */
/*                                                                  */
/*  Content: { imageData, width, height, fallback }                 */
/*    imageData — data URL from loadImageAsDataUrl                  */
/*    width/height — natural pixel dimensions (for 'contain' fit)   */
/*    fallback — optional { type: 'placeholder'|'text', ... }       */
/*                                                                  */
/*  fit modes:                                                      */
/*    'cover' — fill box, overflow cropped (caller pre-crops)       */
/*    'contain' — scale to fit, leave whitespace (default for logo) */
/* ─────────────────────────────────────────────────────────────── */

export function renderImageTo(pdf, element, content, ctx) {
  const props = element.props || {};
  const fit = props.fit || 'cover';

  if (!content?.imageData) {
    // Apply fallback
    if (content?.fallback?.type === 'text') {
      renderTextTo(pdf, element, { text: content.fallback.text }, ctx);
    } else if (content?.fallback?.type === 'placeholder' && content.fallback.draw) {
      // Caller supplies a draw function bound to the placeholder logic
      content.fallback.draw(pdf, element);
    }
    return;
  }

  if (fit === 'contain' && content.width && content.height) {
    const ratio = Math.min(element.w / content.width, element.h / content.height);
    const w = content.width * ratio;
    const h = content.height * ratio;
    // Top-left alignment within the box (template-minimal behaviour)
    const x = element.x;
    const y = element.y;
    pdf.addImage(content.imageData, content.format || 'PNG', x, y, w, h, undefined, 'FAST');
  } else {
    // cover: caller pre-cropped to exact dims, just embed
    pdf.addImage(
      content.imageData,
      content.format || 'PNG',
      element.x,
      element.y,
      element.w,
      element.h,
      undefined,
      'FAST',
    );
  }
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: QR                                                   */
/*                                                                  */
/*  Content: { qrDataUrl }                                          */
/*                                                                  */
/*  QR is just an image box underneath; this wrapper exists so the  */
/*  editor can enforce "QR cannot be smaller than 18×18mm" and      */
/*  future tweaks (error-correction levels, dark/light recolour)    */
/*  have a single home.                                             */
/* ─────────────────────────────────────────────────────────────── */

export function renderQRTo(pdf, element, content, ctx) {
  if (!content?.qrDataUrl) return;

  // Enforce minimum size at render time as a safety net. Editor
  // should prevent this at save time, but downloads from malformed
  // saved JSON shouldn't render an unscannable card.
  const minSize = 18;
  const size = Math.min(element.w, element.h);
  if (size < minSize) {
    console.warn(
      `[render-layout] QR element ${element.id} is ${size}mm × ${size}mm, ` +
      `below ${minSize}mm scan threshold. Bumping to ${minSize}mm.`,
    );
  }
  const renderW = Math.max(element.w, minSize);
  const renderH = Math.max(element.h, minSize);

  pdf.addImage(content.qrDataUrl, 'PNG', element.x, element.y, renderW, renderH, undefined, 'FAST');
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: card background                                      */
/*                                                                  */
/*  Draws the white (or layout-specified) background filling the    */
/*  entire card. Called once before any elements render.            */
/* ─────────────────────────────────────────────────────────────── */

export function renderBackground(pdf, layout) {
  const bg = layout?.canvas?.background || '#FFFFFF';
  const rgb = hexToRgb(bg);
  pdf.setFillColor(rgb.r, rgb.g, rgb.b);
  pdf.rect(0, 0, CR80.widthMm, CR80.heightMm, 'F');
}

/* ─────────────────────────────────────────────────────────────── */
/*  Utility: sort elements by z-order for draw sequence             */
/* ─────────────────────────────────────────────────────────────── */

export function sortByZ(elements) {
  return [...elements].sort((a, b) => (a.z || 0) - (b.z || 0));
}
